/**
 * eReg Context
 *
 * State management for the eReg platform.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import * as eregApi from '../lib/api';
import type {
  Protocol,
  Folder,
  DocumentType,
  User,
} from '../types';

interface EregContextValue {
  // User state
  user: User | null;
  loading: boolean;
  isAdmin: boolean;

  // Protocols
  protocols: Protocol[];
  protocolsLoading: boolean;
  currentProtocol: Protocol | null;
  setCurrentProtocol: (protocol: Protocol | null) => void;
  refreshProtocols: () => Promise<void>;

  // Folders
  folders: Folder[];
  foldersLoading: boolean;
  currentFolder: Folder | null;
  setCurrentFolder: (folder: Folder | null) => void;
  refreshFolders: (protocolId?: string) => Promise<void>;

  // Document types
  documentTypes: DocumentType[];
  documentTypesLoading: boolean;
  refreshDocumentTypes: () => Promise<void>;

  // Navigation
  siteMode: boolean; // True when viewing site-level documents
  setSiteMode: (mode: boolean) => void;
}

const EregContext = createContext<EregContextValue | undefined>(undefined);

// The folders APIs return a NESTED tree (roots with children[]). Pages derive
// hierarchy from parent_folder_id / path against a FLAT list, so flatten once
// here (depth-first keeps hierarchical order for pickers) and drop the nested
// copies — keeping them around meant subfolders never rendered anywhere.
function flattenFolderTree(nodes: Folder[]): Folder[] {
  const flat: Folder[] = [];
  const walk = (items: Folder[]) => {
    for (const item of items) {
      const { children: kids, ...folder } = item;
      flat.push(folder as Folder);
      if (kids && kids.length > 0) walk(kids);
    }
  };
  walk(nodes || []);
  return flat;
}

export function EregProvider({ children }: { children: React.ReactNode }) {
  const { user: authUser, currentSite, isAdmin: authIsAdmin, isSuperAdmin } = useAuth();

  // User state
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = authIsAdmin || isSuperAdmin;

  // Protocol state
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [protocolsLoading, setProtocolsLoading] = useState(false);
  const [currentProtocol, setCurrentProtocolState] = useState<Protocol | null>(null);

  // Folder state
  const [folders, setFolders] = useState<Folder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [currentFolder, setCurrentFolder] = useState<Folder | null>(null);

  // Document types
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [documentTypesLoading, setDocumentTypesLoading] = useState(false);

  // Navigation
  const [siteMode, setSiteModeState] = useState(false);

  // A folder selection must never outlive its study/site scope: a stale
  // currentFolder rendered the previous study's documents and seeded Upload's
  // folder_id across studies.
  const setCurrentProtocol = (protocol: Protocol | null) => {
    if ((protocol?.id || null) !== (currentProtocol?.id || null)) {
      setCurrentFolder(null);
    }
    if (protocol) setSiteModeState(false);
    setCurrentProtocolState(protocol);
  };

  const setSiteMode = (mode: boolean) => {
    if (mode !== siteMode) {
      setCurrentFolder(null);
    }
    if (mode) setCurrentProtocolState(null);
    setSiteModeState(mode);
  };

  // Track if initial data has been fetched to avoid refetching on token refresh
  const lastAuthUserId = useRef<string | null>(null);

  // Load user data - only when authUser actually changes
  useEffect(() => {
    if (authUser) {
      // Skip if this is the same user we already loaded
      if (lastAuthUserId.current === authUser.id) {
        return;
      }
      lastAuthUserId.current = authUser.id;
      setUser({
        id: authUser.id,
        full_name: authUser.full_name || '',
        email: authUser.email || '',
        role: authUser.role,
      });
      setLoading(false);
    } else {
      lastAuthUserId.current = null;
      setUser(null);
      setLoading(true);
    }
  }, [authUser]);

  // Load protocols
  const refreshProtocols = useCallback(async () => {
    setProtocolsLoading(true);
    try {
      const data = await eregApi.getProtocols();
      setProtocols(data);
    } catch (error) {
      console.error('[eReg] Error loading protocols:', error);
    } finally {
      setProtocolsLoading(false);
    }
  }, []);

  // Load protocols at login and again on every site switch — the list is
  // cross-site, but a switch is the natural staleness boundary (audit N-3:
  // it used to load once per login and never again). `user` is only replaced
  // when a different account signs in, so token refreshes don't refetch.
  useEffect(() => {
    if (user) {
      refreshProtocols();
    }
  }, [user, currentSite?.id, refreshProtocols]);

  // A site switch also drops a selected study that belongs to another site
  // (N-3: it used to survive silently, leaving you operating on the old
  // site's study). Only real currentSite.id CHANGES count — a deep link
  // opening another site's study is not a switch and is left alone.
  const lastSiteIdRef = useRef<string | null>(null);
  useEffect(() => {
    const siteId = currentSite?.id ?? null;
    const previous = lastSiteIdRef.current;
    lastSiteIdRef.current = siteId;
    if (previous === null || siteId === null || previous === siteId) return;
    if (currentProtocol?.site_id && currentProtocol.site_id !== siteId) {
      setCurrentProtocol(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSite?.id]);

  // Load folders for current protocol or site
  const refreshFolders = useCallback(async (protocolId?: string) => {
    setFoldersLoading(true);
    try {
      let data: Folder[];
      if (protocolId) {
        data = await eregApi.getProtocolFolders(protocolId);
      } else if (currentSite && siteMode) {
        data = await eregApi.getSiteFolders(currentSite.id);
      } else if (currentProtocol) {
        data = await eregApi.getProtocolFolders(currentProtocol.id);
      } else {
        data = [];
      }
      setFolders(flattenFolderTree(data));
    } catch (error) {
      console.error('[eReg] Error loading folders:', error);
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  }, [currentSite, currentProtocol, siteMode]);

  // Refresh folders when protocol or mode changes
  useEffect(() => {
    if (currentProtocol || siteMode) {
      refreshFolders();
    }
  }, [currentProtocol, siteMode, refreshFolders]);

  // Load document types
  const refreshDocumentTypes = useCallback(async () => {
    setDocumentTypesLoading(true);
    try {
      const data = await eregApi.getDocumentTypes({
        network_id: currentSite?.network_id,
        site_id: currentSite?.id,
      });
      setDocumentTypes(data);
    } catch (error) {
      console.error('[eReg] Error loading document types:', error);
    } finally {
      setDocumentTypesLoading(false);
    }
  }, [currentSite]);

  // Load document types on mount
  useEffect(() => {
    if (user && currentSite) {
      refreshDocumentTypes();
    }
  }, [user, currentSite, refreshDocumentTypes]);

  const value: EregContextValue = {
    user,
    loading,
    isAdmin,
    protocols,
    protocolsLoading,
    currentProtocol,
    setCurrentProtocol,
    refreshProtocols,
    folders,
    foldersLoading,
    currentFolder,
    setCurrentFolder,
    refreshFolders,
    documentTypes,
    documentTypesLoading,
    refreshDocumentTypes,
    siteMode,
    setSiteMode,
  };

  return (
    <EregContext.Provider value={value}>
      {children}
    </EregContext.Provider>
  );
}

export function useEreg() {
  const context = useContext(EregContext);
  if (!context) {
    throw new Error('useEreg must be used within an EregProvider');
  }
  return context;
}

export default EregContext;
